import * as homeReducer from "containers/Home/reducer";
import * as setupReducer from "containers/Setup/reducer";

const UPDATE_TRANSFORM = "transform/UPDATE_TRANSFORM";
const UPDATE_TRANSFORMS = "transform/UPDATE_TRANSFORMS";
const UPDATE_FIELD = "transform/UPDATE_FIELD";
const UPDATE_FIELD_FIELD = "transform/UPDATE_FIELD_FIELD";
const UPDATE_FIELD_MANY = "transform/UPDATE_FIELD_MANY";

const initialState = {};

function globalReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_TRANSFORM:
      return {
        ...state,
        [action.id]: action.data
      };
    case UPDATE_TRANSFORMS:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.index]: action.data
        }
      };
    case UPDATE_FIELD_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.index]: {
            ...(state?.[action.id]?.[action.index] || {
              fieldOne: undefined,
              fieldTwo: undefined,
              id: 0,
              operation: undefined,
              saved: true
            }),
            [action.key]: action.data
          }
        }
      };
    case UPDATE_FIELD_MANY:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.index]: {
            ...state[action.id][action.index],
            ...action.data
          }
        }
      };
    default:
      return state;
  }
}

/* Selectors */
export function getBase(state) {
  return state.transform;
}

export function getTransform(state, id) {
  return getBase(state)[id] ? getBase(state)[id] : {};
}

export function getTransformArr(state, id, showDeleted) {
  const transform = getTransform(state, id) || {};

  return Object.values(transform).filter((k) => k && (showDeleted || k.deleted !== true));
}

export function getNewTransformId(state, id) {
  const transforms = getTransformArr(state, id, true);

  const highestId = transforms?.reduce((currHighest, t) => {
    return Math.max(t.id, currHighest);
  }, -1);

  return highestId + 1;
}

export function getCalculatedFields(state, id, showDeleted) {
  return getTransformArr(state, id, showDeleted).filter(
    (t) => t.specializedAI === undefined && t.expressionType === undefined
  );
}

export function getExpressionFields(state, id, showDeleted, expressionType = "unary") {
  return getTransformArr(state, id, showDeleted).filter(
    (t) => t.specializedAI === undefined && t.expressionType === expressionType
  );
}

export function getSpecializedAIFields(state, id, showDeleted) {
  const fields = getTransformArr(state, id, showDeleted)
    .filter((t) => t.specializedAI !== undefined)
    .map((t) => ({
      label: (t.fieldOne || {}).value,
      sType: t.operation,
      uType: t.uType,
      uOption: t.uOption,
      probability: t.probability,
      id: t.id,
      isManual: t.isManual
    }));

  const dataset = setupReducer.getDatasetFromSetup(state, id);

  const datasetFields = Object.values((dataset || {}).fields || {})
    .flatMap((field) => field.transformedInclusionProbabilities || [])
    .map((val) => ({
      label: val.name,
      sType: val.operation,
      uType: "Generate Helper AI Field",
      uOption: "Skip",
      probability: val.probability,
      id: val.name,
      isManual: false
    }))
    .map((val) => fields.find((f) => f.id == val.id) || val);

  return [...datasetFields, ...fields.filter((val) => datasetFields.find((f) => f.id == val.id) === undefined)];
}

export function getTransformRequest(state, id) {
  const transform = getTransformArr(state, id);

  return transform
    .filter((t) => t.saved === true && t.fieldOne && t.fieldTwo)
    .map((t) => [t.fieldOne.value, t.fieldTwo.value, t.operation ? t.operation.value || t.operation : "CONCAT"]);
}

export function getFields(state, id) {
  return getTransform(state, id).fields || {};
}

export function getFieldIndex(state, id, index) {
  return getTransform(state, id)?.[index] || {};
}

export function getFieldsArr(state, id) {
  const fields = getFields(state, id);
  return Object.keys(fields).map((k) => fields[k]);
}

/* Actions */
export function addField(id, forceOperation = undefined, field, extra = {}, fieldId) {
  return (dispatch, getState) => {
    const state = getState();

    const initField = {
      ...extra,
      fieldOne: field && field.one ? { value: field.one, label: field.one } : undefined,
      fieldTwo: field && field.two ? { value: field.two, label: field.two } : undefined,
      operation: forceOperation ? forceOperation : undefined,
      saved: extra && extra.saved === false ? false : true,
      id: fieldId === undefined ? getNewTransformId(state, id) : fieldId
    };

    const transform = {
      ...getTransform(state, id),
      [initField.id]: initField
    };

    return dispatch(updateTransform(id, transform));
  };
}

export function addSpecializedAIField(id, extra = {}, fieldId) {
  return (dispatch, getState) => {
    const data = { ...extra };

    let field = undefined;

    if (data.label !== undefined) {
      field = { one: data.label, two: data.label };

      data.label = undefined;
    }

    return dispatch(
      addField(
        id,
        extra.sType,
        field,
        {
          specializedAI: true,
          uOption: extra.uOption || "Skip",
          ...data
        },
        fieldId
      )
    );
  };
}

export function updateSpecializedAIField(id, index, update = {}) {
  return (dispatch, getState) => {
    const transform = getTransform(getState(), id)[index];

    if (transform === undefined) {
      const field = getSpecializedAIFields(getState(), id).find((f) => f.id == index);

      dispatch(addSpecializedAIField(id, field, field.id));
    }

    const data = {};

    if (update.label !== undefined) {
      data.fieldOne = { value: update.label, label: update.label };
      data.fieldTwo = data.fieldOne;
    }

    if (update.sType !== undefined) {
      data.operation = update.sType;
    }

    if (update.uType !== undefined) {
      data.uType = update.uType;
    }

    if (update.uOption !== undefined) {
      data.uOption = update.uOption;
    }

    if (update.deleted) {
      data.deleted = update.deleted;
    }

    return dispatch({
      type: UPDATE_FIELD_MANY,
      id: id,
      index: index,
      data: data
    });
  };
}

export function addInteractions(setupId, datasetId, saved = true) {
  return (dispatch, getState) => {
    const interactions = homeReducer.getInteractions(getState(), datasetId);
    const transforms = getTransformArr(getState(), setupId);

    let maxAutoId = -1;

    transforms.forEach((t) => {
      if (t.interactionId !== undefined && t.interactionId > maxAutoId) {
        maxAutoId = t.interactionId;
      }
    });

    interactions
      .filter((interaction, i) => i > maxAutoId && i <= maxAutoId + 5)
      .forEach((interaction, i) => {
        dispatch(
          addField(
            setupId,
            { value: "CONCAT", label: "Interaction" },
            {
              one: interaction.data.fields[0],
              two: interaction.data.fields[1]
            },
            { interactionId: i + maxAutoId + 1, saved: saved }
          )
        );
      });
  };
}

export function updateTransform(id, transform) {
  return { type: UPDATE_TRANSFORM, id: id, data: transform };
}

export function updateTransforms(transforms) {
  return { type: UPDATE_TRANSFORMS, data: transforms };
}

export function deleteTransform(id, index) {
  return {
    type: UPDATE_FIELD_FIELD,
    id: id,
    index: index,
    data: true,
    key: "deleted"
  };
}

export function updateExpressionField(id, index, field, expressionType) {
  return (dispatch, getState) => {
    if (expressionType) {
      const eType = getFieldIndex(getState(), id, index)?.expressionType;
      if (eType !== expressionType) {
        dispatch({
          type: UPDATE_FIELD_FIELD,
          id: id,
          index: index,
          key: "expressionType",
          data: expressionType
        });
      }
      dispatch(updateOperation(id, index, undefined, expressionType));
    }

    return dispatch(updateFieldOne(id, index, field));
  };
}

export function updateFieldOne(id, index, field) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_FIELD_FIELD,
      id: id,
      index: index,
      key: "fieldOne",
      data: field
    });

    return dispatch(buildDefaultColumnLabel(id, index));
  };
}

export function updateFieldTwo(id, index, field) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_FIELD_FIELD,
      id: id,
      index: index,
      key: "fieldTwo",
      data: field
    });

    return dispatch(buildDefaultColumnLabel(id, index));
  };
}

export function updateExpressionOperation(id, index, operation, expressionType) {
  return (dispatch, getState) => {
    if (expressionType) {
      const eType = getFieldIndex(getState(), id, index)?.expressionType;
      if (eType !== expressionType) {
        dispatch({
          type: UPDATE_FIELD_FIELD,
          id: id,
          index: index,
          key: "expressionType",
          data: expressionType
        });
      }
    }

    return dispatch(updateOperation(id, index, operation));
  };
}

export function updateOperation(id, index, operation) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_FIELD_FIELD,
      id: id,
      index: index,
      key: "operation",
      data: operation
    });

    return dispatch(buildDefaultColumnLabel(id, index));
  };
}

export function buildDefaultColumnLabel(id, index) {
  return (dispatch, getState) => {
    const calcField = getFieldIndex(getState(), id, index);

    const expressionType = calcField.expressionType;
    const noSecondField = expressionType === "unary" || expressionType === "extract_date_part";

    // Update default column label
    if (
      calcField?.fieldOne !== undefined &&
      (calcField?.fieldTwo !== undefined || noSecondField) &&
      calcField?.operation !== undefined &&
      calcField?.columnLabel === undefined
    ) {
      const columnLabel =
        calcField?.fieldOne?.label +
        "_" +
        calcField?.operation?.label +
        (noSecondField ? "" : "_" + calcField?.fieldTwo?.label);

      dispatch(updateColumnLabel(id, index, columnLabel));
    }
  };
}

export function updateSaved(id, index, saved) {
  return {
    type: UPDATE_FIELD_FIELD,
    id: id,
    index: index,
    key: "saved",
    data: saved
  };
}

export function updateColumnLabel(id, index, columnLabel) {
  return {
    type: UPDATE_FIELD_FIELD,
    id: id,
    index: index,
    key: "columnLabel",
    data: columnLabel
  };
}

export default globalReducer;
